<template>
  <div class="article-preview" :class="{ 'mode-html': tab === 'HTML' }">
    <div class="article-preview-toolbar">
      <div class="dots"><span></span><span></span><span></span></div>
      <div class="url-bar"><span class="lock">🔒</span>{{ previewUrl }}</div>
      <div class="preview-tabs">
        <button v-for="t in TABS" :key="t" class="preview-tab-btn" :class="{ active: tab === t }" @click="tab = t">{{ t }}</button>
      </div>
    </div>

    <!-- Publish parity, minus the Start over button. -->
    <div v-if="tab !== 'HTML' && editIndex === -1" class="preview-controls-bar">
      <label class="kw-toggle" @click="highlight = !highlight">
        <span class="sw" :class="{ on: highlight }"><span class="knob"></span></span>
        Highlight keywords
      </label>
    </div>

    <div v-if="tab === 'HTML'" class="article-html-wrap">
      <!-- HTML edit mode: dark mono textarea + explicit save (Draft-step UX) -->
      <template v-if="htmlEditing">
        <div class="editing-area html-editing-area">
          <textarea ref="htmlTa" class="edit-textarea html-edit-textarea" v-model="htmlDraft" spellcheck="false" @input="autoSizeHtmlTa"></textarea>
          <div class="edit-mode-actions">
            <button type="button" class="wg-btn wg-btn--primary" @click="saveHtmlEdit">Save changes</button>
            <button type="button" class="wg-btn" @click="htmlEditing = false">Cancel</button>
          </div>
        </div>
      </template>
      <template v-else>
        <button class="html-copy-btn" @click="copyHtml">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>
          Copy HTML
        </button>
        <pre class="article-html-view" title="Double-click to edit" @dblclick="startHtmlEdit">{{ beautified }}</pre>
      </template>
    </div>
    <!-- Desktop: each top-level element (h2/h3/p/list/table/…) is individually
         editable (Draft-step UX: dblclick -> orange-ring textarea for that
         element only). Mobile renders the whole article read-only. -->
    <article v-else class="article-body" :class="{ mobile: tab === 'Mobile' }">
      <template v-if="tab === 'Desktop'">
        <template v-for="(seg, i) in displaySegments">
          <div v-if="editIndex === i" :key="'edit' + i" class="editing-area">
            <!-- Rendered WYSIWYG surface (no raw tags): innerHTML set once on
                 edit start, read back on save — inline markup preserved. -->
            <div ref="editSurface" class="edit-surface" contenteditable="true" spellcheck="false"></div>
            <div class="edit-mode-actions">
              <button type="button" class="wg-btn wg-btn--primary" @click="saveEdit">Save changes</button>
              <button type="button" class="wg-btn" @click="editIndex = -1">Cancel</button>
              <button type="button" class="wg-btn wg-btn--danger" title="Remove this element from the article" @click="deleteEdit">Delete</button>
            </div>
          </div>
          <div
            v-else
            :key="'seg' + i"
            class="wg-block"
            :class="{ 'wg-block-system': isTocIndex(i) }"
            :title="isTocIndex(i) ? 'Table of contents — auto-built from headings' : 'Double-click to edit'"
            @dblclick="startEdit(i)"
            v-html="seg"
          ></div>
        </template>
      </template>
      <div v-else class="art-content" v-html="renderedBody"></div>
    </article>
  </div>
</template>

<script>
// Read-only article preview — 1:1 with the Publish step's .article-preview
// (browser chrome + Desktop/Mobile/HTML tabs + keyword highlight + Copy HTML),
// without the Start over button. Renders the editor-provided head/body HTML.
import { highlightHtml } from "../AiWriter/v2/steps/publish/exporters";
import { beautifyHtmlString } from "@/components/FullPageSourceEditorV2/components/AiWriter/helper";

const TABS = ["Desktop", "Mobile", "HTML"];

export default {
  name: "WhiteGloveArticlePreview",
  props: {
    keyword: { type: String, default: "" },
    wysiwygBody: { type: String, default: "" },
    wysiwygHead: { type: String, default: "" },
    pageRun: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      TABS,
      tab: "Desktop",
      highlight: false,
      // Draft-step edit UX: dblclick -> textarea swap, explicit save/cancel.
      // Desktop edits ONE top-level element at a time (editIndex into segments);
      // the HTML tab edits the full source. Mobile is read-only by design.
      editIndex: -1,
      htmlEditing: false,
      htmlDraft: "",
    };
  },
  watch: {
    // Leaving a tab / toggling highlight cancels any in-progress edit
    // (nothing is emitted).
    tab() {
      this.editIndex = -1;
      this.htmlEditing = false;
    },
    highlight() {
      this.editIndex = -1;
    },
  },
  computed: {
    previewUrl() {
      return (this.pageRun && this.pageRun.url) || "https://";
    },
    // Keyword + report variations (when available) — the highlight vocabulary.
    terms() {
      const out = this.keyword ? [this.keyword] : [];
      const vars = this.pageRun && this.pageRun.variations;
      if (Array.isArray(vars)) vars.forEach((v) => { if (typeof v === "string" && v) out.push(v); });
      return out;
    },
    renderedBody() {
      if (this.highlight) {
        return highlightHtml(this.wysiwygBody || "", this.terms, (m) => `<mark class="kw-hl">${m}</mark>`);
      }
      return this.wysiwygBody || "";
    },
    // The article's top-level nodes, one editable segment each. Elements keep
    // their outerHTML; loose non-whitespace text nodes become their own segment
    // so a join() reassembles the body byte-identically (order preserved).
    // The Key-takeaways pair (marker h3 + its <ul>) merges into ONE segment so
    // it is edited together and displayed as the publish gold box.
    segments() {
      const doc = new DOMParser().parseFromString(this.wysiwygBody || "", "text/html");
      const nodes = [];
      doc.body.childNodes.forEach((n) => {
        if (n.nodeType === Node.ELEMENT_NODE) nodes.push(n);
        else if (n.nodeType === Node.TEXT_NODE && n.textContent.trim()) nodes.push(n);
      });
      const isKtTitle = (n) =>
        n.nodeType === Node.ELEMENT_NODE &&
        n.tagName === "H3" &&
        (n.classList.contains("pop-key-takeaways-title") ||
          n.textContent.trim().toLowerCase() === "key takeaways");
      const isTocTitle = (n) =>
        n.nodeType === Node.ELEMENT_NODE &&
        n.tagName === "H2" &&
        n.classList.contains("pop-toc-title");
      const out = [];
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const next = nodes[i + 1];
        if ((isKtTitle(n) || isTocTitle(n)) && next && next.nodeType === Node.ELEMENT_NODE && next.tagName === "UL") {
          out.push(n.outerHTML + next.outerHTML);
          i++;
          continue;
        }
        out.push(n.nodeType === Node.ELEMENT_NODE ? n.outerHTML : n.textContent);
      }
      return out;
    },
    // Display variant: publish-design transform for the takeaways segment and
    // keyword highlight, both display-only — edits always start from the raw
    // segment and the saved HTML stays editor-safe.
    displaySegments() {
      const ktRe = /^<h3\b[^>]*>[\s\S]*?<\/h3>\s*(<ul[\s\S]*<\/ul>)\s*$/i;
      const ktStart = /^<h3\b[^>]*(?:pop-key-takeaways-title|>\s*Key takeaways\s*<)/i;
      return this.segments.map((s) => {
        let disp = s;
        if (ktStart.test(s)) {
          const m = ktRe.exec(s);
          if (m) disp = `<div class="article-takeaways"><h4>⚡ Key takeaways</h4>${m[1]}</div>`;
        } else if (this.isTocSegment(s)) {
          // Display as the publish nav.pop-toc (label + numbered list).
          const label = (/^<h2\b[^>]*>([\s\S]*?)<\/h2>/i.exec(s) || [])[1] || "In this article";
          const list = (/<ul\b[\s\S]*<\/ul>/i.exec(s) || [])[0] || "";
          const lis = list
            .replace(/^<ul\b[^>]*>/i, "")
            .replace(/<\/ul>$/i, "")
            .replace(/class="pop-toc-sub"/gi, 'class="toc-sub"');
          disp = `<nav class="pop-toc" aria-label="Table of contents"><h2>${label}</h2><ol class="toc-list">${lis}</ol></nav>`;
        }
        if (this.highlight) disp = highlightHtml(disp, this.terms, (m) => `<mark class="kw-hl">${m}</mark>`);
        return disp;
      });
    },
    fullHtmlSource() {
      return `<html>\n<head>\n${this.wysiwygHead || ""}\n</head>\n<body>\n${this.wysiwygBody || ""}\n</body>\n</html>`;
    },
    beautified() {
      return beautifyHtmlString(this.fullHtmlSource);
    },
  },
  methods: {
    copyHtml() {
      if (navigator.clipboard) navigator.clipboard.writeText(this.beautified);
      if (this.$notify) this.$notify({ group: "info", type: "success", text: "HTML copied to clipboard" });
    },
    // Desktop view: edit ONE element in a rendered contenteditable surface (raw
    // segment — never the keyword-highlighted variant, so highlight marks can't
    // leak into the saved content). innerHTML is set once here and read back on
    // save; while typing the DOM owns the content (Vue never re-binds it).
    // The surface ref lives inside a v-for, so Vue 2 registers it as an array
    // (even though only one edit surface renders at a time).
    editSurfaceEl() {
      const r = this.$refs.editSurface;
      return Array.isArray(r) ? r[0] : r;
    },
    // The TOC segment is system-built — never hand-edited.
    isTocSegment(s) {
      return /^<h2\b[^>]*pop-toc-title/i.test(s || "");
    },
    isTocIndex(i) {
      return this.isTocSegment(this.segments[i]);
    },
    startEdit(i) {
      if (this.tab !== "Desktop") return;
      if (this.isTocIndex(i)) return;
      this.editIndex = i;
      this.$nextTick(() => {
        const el = this.editSurfaceEl();
        if (!el) return;
        el.innerHTML = this.segments[i] || "";
        el.focus();
      });
    },
    saveEdit() {
      const i = this.editIndex;
      const el = this.editSurfaceEl();
      this.editIndex = -1;
      if (i < 0 || !el) return;
      const html = el.innerHTML;
      // Saving an emptied element must not leave a blank gap — treat it as a
      // delete (no text and nothing content-bearing like an image or table).
      const tpl = document.createElement("template");
      tpl.innerHTML = html;
      const hasText = !!(tpl.content.textContent || "").trim();
      const hasMedia = /<(img|table|iframe|form)\b/i.test(html);
      if (!hasText && !hasMedia) return this.removeSegmentAt(i);
      const segs = this.segments.slice();
      segs[i] = html;
      this.resyncToc(segs);
      this.$emit("update:wysiwygBody", segs.join("\n"));
      this.$emit("content-changed");
    },
    // Explicit Delete button in the edit box (and the empty-save path above).
    deleteEdit() {
      const i = this.editIndex;
      this.editIndex = -1;
      if (i < 0) return;
      this.removeSegmentAt(i);
    },
    removeSegmentAt(i) {
      const segs = this.segments.slice();
      segs.splice(i, 1);
      this.resyncToc(segs);
      this.$emit("update:wysiwygBody", segs.join("\n"));
      this.$emit("content-changed");
    },
    // Auto-sync (spec): after any edit, rebuild the TOC links from the CURRENT
    // H2 headings (skip FAQ/TOC titles). Under 3 headings, leave the markers in
    // place so the list can resync when headings return.
    resyncToc(segs) {
      const ti = segs.findIndex((s) => this.isTocSegment(s));
      if (ti < 0) return segs;
      // Preserve the H3 sub-level only if the current TOC shows it.
      const withSubs = /pop-toc-sub/.test(segs[ti]);
      const stripTags = (html) => {
        const tpl = document.createElement("template");
        tpl.innerHTML = html;
        return (tpl.content.textContent || "").trim();
      };
      // Visual-block titles are headings in the editor copy but must never
      // enter the TOC (publish's build_toc reads only section headings): a lone
      // h3 followed by a marked list/table is that visual's title, and a lone
      // h2 with the sources list ahead is the "References" head.
      const isVisualTitleSeg = (idx) => {
        const seg = (segs[idx] || "").trim();
        if (/^<h3\b[^>]*>[\s\S]*<\/h3>$/i.test(seg)) {
          const nxt = (segs[idx + 1] || "").trim();
          return /^<(ul|ol)\b[^>]*pop-article-list|^<table\b[^>]*pop-article-table/i.test(nxt);
        }
        if (/^<h2\b[^>]*>[\s\S]*<\/h2>$/i.test(seg)) {
          const ahead = (segs[idx + 1] || "") + (segs[idx + 2] || "");
          return /dvb-sources/i.test(ahead);
        }
        return false;
      };
      const heads = []; // [{text, subs: []}]
      let skipping = false; // a skipped H2 (e.g. FAQ) swallows its H3s too
      segs.forEach((s, idx) => {
        if (idx === ti || isVisualTitleSeg(idx)) return;
        // The References section can be ONE segment (section.article-sources
        // wrapping h2 + note + ol.dvb-sources) — none of its headings are TOC
        // content, so skip the whole segment.
        if (/dvb-sources|article-sources/i.test(s)) return;
        const re = /<(h2|h3)\b[^>]*>([\s\S]*?)<\/\1>/gi;
        let m;
        while ((m = re.exec(s))) {
          const text = stripTags(m[2]);
          const lower = text.toLowerCase();
          const skip =
            /pop-faq-title|pop-toc-title|pop-key-takeaways-title|pop-faq-q/.test(m[0]) ||
            !text || lower === "references" ||
            lower.indexOf("frequently asked questions") === 0 || /^faqs?\b/.test(lower);
          if (m[1].toLowerCase() === "h2") {
            skipping = skip;
            if (!skip) heads.push({ text, subs: [] });
          } else if (withSubs && heads.length && !skipping && !skip) {
            heads[heads.length - 1].subs.push(text);
          }
        }
      });
      if (heads.length < 3) return segs;
      const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      const esc = (t) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
      const label = (/^<h2\b[^>]*>([\s\S]*?)<\/h2>/i.exec(segs[ti]) || [])[1] || "In this article";
      const lis = heads.map((h) => {
        const sub = h.subs.length
          ? `<ul class="pop-toc-sub">${h.subs.map((t) => `<li><a href="#${slug(t)}">${esc(t)}</a></li>`).join("")}</ul>`
          : "";
        return `<li><a href="#${slug(h.text)}">${esc(h.text)}</a>${sub}</li>`;
      }).join("");
      segs[ti] = `<h2 class="pop-toc-title">${label}</h2><ul class="pop-toc-list">${lis}</ul>`;
      return segs;
    },
    // HTML view: edit the assembled source; the body inner HTML is extracted on
    // save (a source without <body> tags is treated as the body itself).
    startHtmlEdit() {
      this.htmlDraft = this.beautified;
      this.htmlEditing = true;
      this.$nextTick(this.autoSizeHtmlTa);
    },
    // The HTML textarea grows with its content (no inner scrollbar) — the page
    // scrolls instead.
    autoSizeHtmlTa() {
      const el = this.$refs.htmlTa;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = el.scrollHeight + 2 + "px";
    },
    saveHtmlEdit() {
      this.htmlEditing = false;
      // Head edits (<title>, meta description) must persist too — the parent's
      // saved document is composed from wysiwygHead + wysiwygBody.
      const h = /<head[^>]*>([\s\S]*?)<\/head>/i.exec(this.htmlDraft || "");
      if (h) this.$emit("update:wysiwygHead", h[1].trim());
      const m = /<body[^>]*>([\s\S]*?)<\/body>/i.exec(this.htmlDraft || "");
      const body = (m ? m[1] : this.htmlDraft || "").trim();
      this.$emit("update:wysiwygBody", body);
      this.$emit("content-changed");
    },
  },
};
</script>

<style scoped>
/* 1:1 copy of the Publish step's .article-preview design (POP tokens). */
.article-preview { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); overflow: hidden; display: flex; flex-direction: column; font-family: "Mulish", system-ui, sans-serif; }
.article-preview-toolbar { display: flex; align-items: center; gap: 10px; padding: 10px 16px; background: var(--surface-2, #f7f7f7); border-bottom: 1px solid var(--border, #e4e4e4); }
.article-preview-toolbar .dots { display: flex; gap: 5px; }
.article-preview-toolbar .dots span { width: 9px; height: 9px; border-radius: 50%; background: var(--border-strong, #ccc); }
.article-preview-toolbar .url-bar { flex: 1; background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: 5px; padding: 4px 10px; font-size: 11.5px; color: var(--text-3, #6b7b7e); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.article-preview-toolbar .url-bar .lock { color: var(--pop-green, #25a97a); margin-right: 5px; }
.article-preview-toolbar .preview-tabs { display: flex; background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: 5px; padding: 2px; gap: 1px; }
.preview-tab-btn { background: transparent; border: none; padding: 4px 10px; font-family: inherit; font-size: 11px; font-weight: 600; color: var(--text-3, #6b7b7e); cursor: pointer; border-radius: 3px; }
.preview-tab-btn.active { background: var(--pop-ink, #012323); color: #fff; }
.preview-controls-bar { display: flex; align-items: center; gap: 12px; padding: 10px 16px; background: var(--surface-2, #f7f7f7); border-bottom: 1px solid var(--border, #e4e4e4); }
.kw-toggle { display: inline-flex; align-items: center; gap: 9px; font-size: 13px; font-weight: 600; color: var(--text-2, #014751); cursor: pointer; margin-right: auto; }
.kw-toggle .sw { width: 38px; height: 22px; border-radius: 999px; position: relative; flex: none; transition: background .15s; background: #b5b5b5; }
.kw-toggle .sw .knob { position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.3); transition: left .15s; }
.kw-toggle .sw.on { background: #25a97a; }
.kw-toggle .sw.on .knob { left: 18px; }

/* HTML view */
.article-html-wrap { position: relative; }
.html-copy-btn { position: absolute; top: 12px; right: 16px; z-index: 2; display: inline-flex; align-items: center; gap: 6px; background: rgba(255,255,255,.12); color: #fff; border: 1px solid rgba(255,255,255,.25); font-family: inherit; font-size: 12px; font-weight: 700; padding: 6px 12px; border-radius: 7px; cursor: pointer; transition: background .12s; }
.html-copy-btn:hover { background: rgba(255,255,255,.22); }
.html-copy-btn svg { width: 14px; height: 14px; }
.article-html-view { margin: 0; padding: 18px 20px; background: #0f1f24; color: #cfe5ea; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; line-height: 1.6; white-space: pre-wrap; word-break: break-word; }

/* Article body (publish typography + design-handoff article elements) */
/* font-size baseline: bare text / inline segments (e.g. a link left over after
   a paragraph edit) inherit paragraph sizing; block tags override per-tag. */
.article-body { padding: 36px 44px 40px; color: #222; line-height: 1.85; font-size: 15.5px; }
.article-body.mobile { max-width: 390px; margin: 0 auto; border-left: 1px solid var(--border, #e4e4e4); border-right: 1px solid var(--border, #e4e4e4); }
@media (max-width: 600px) { .article-body { padding: 24px 22px 28px; } }
.article-body :deep(h1) { font-size: 30px; font-weight: 700; line-height: 1.18; margin: 0 0 16px; color: #111; letter-spacing: -.01em; }
.article-body :deep(h2) { font-size: 22px; font-weight: 700; margin: 32px 0 12px; color: #111; line-height: 1.25; letter-spacing: -.005em; }
.article-body :deep(h3) { font-size: 17.5px; font-weight: 700; margin: 22px 0 8px; color: #222; }
.article-body :deep(p) { margin: 0 0 16px; font-size: 15.5px; color: #333; line-height: 1.85; }
.article-body :deep(a), .article-body :deep(.internal-link) { color: var(--pop-blue, #316bff); text-decoration: underline; text-decoration-color: rgba(0,117,255,.4); font-weight: 600; font-size: inherit; line-height: inherit; }
.article-body :deep(a:hover) { text-decoration-color: var(--pop-blue, #316bff); }
.article-body :deep(ul), .article-body :deep(ol) { margin: 16px 0; padding-left: 24px; font-size: 15px; color: #333; line-height: 1.75; }
.article-body :deep(li) { margin-bottom: 8px; }
.article-body :deep(table) { width: 100%; border-collapse: collapse; margin: 18px 0 22px; font-size: 14px; border: 1px solid #e5e1d7; border-radius: 6px; overflow: hidden; }
.article-body :deep(thead th) { background: #f5efe4; padding: 10px 12px; text-align: left; font-weight: 700; color: #333; font-size: 12.5px; border-bottom: 1px solid #e5e1d7; }
.article-body :deep(tbody td) { padding: 10px 12px; border-bottom: 1px solid #efeae0; color: #444; }
.article-body :deep(tbody tr:nth-child(even)) { background: #fbf9f5; }
.article-body :deep(nav.toc) { background: #fafaf7; border: 1px solid #e8e4d8; border-radius: 8px; padding: 14px 18px; margin: 0 0 22px; font-size: 13.5px; }
.article-body :deep(nav.toc ul) { margin: 0; padding-left: 18px; }
.article-body :deep(mark.kw-hl) { background: #fff24d; color: inherit; font-size: inherit !important; font-weight: inherit; font-family: inherit; line-height: inherit; border-radius: 2px; padding: 0 1px; }
.article-body :deep(img) { max-width: 100%; border-radius: 8px; margin: 18px 0; display: block; }
/* Key takeaways callout */
.article-body :deep(.article-takeaways) { background: linear-gradient(135deg,#FFF7DB 0%,#FDEFC9 55%,#FCE7D6 100%); border: 1px solid rgba(217,178,90,.35); border-radius: 14px; padding: 22px 24px; margin: 24px 0 28px; box-shadow: 0 4px 18px rgba(217,178,90,.14); }
.article-body :deep(.article-takeaways h4) { font-size: 11.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .08em; color: var(--pop-orange-deep,#b23d00); margin: 0 0 14px; display: flex; align-items: center; gap: 7px; }
.article-body :deep(.article-takeaways ul) { margin: 0; padding: 0; list-style: none; font-size: 14.5px; color: #4a3d28; line-height: 1.55; }
.article-body :deep(.article-takeaways li) { position: relative; margin-bottom: 11px; padding-left: 24px; }
.article-body :deep(.article-takeaways li)::before { content: ''; position: absolute; left: 2px; top: 8px; width: 7px; height: 7px; border-radius: 50%; background: linear-gradient(135deg,#F0A030,var(--pop-orange,#ff5700)); }
.article-body :deep(.article-takeaways li:last-child) { margin-bottom: 0; }
/* Boxed list — its <h3> title sits above the box */
.article-body :deep(h4) { font-size: 15.5px; font-weight: 700; margin: 20px 0 6px; color: #222; }
.article-body :deep(h3 + .article-list), .article-body :deep(h4 + .article-list) { margin-top: 8px; }
.article-body :deep(.article-list) { background: #FAFAF7; border: 1px solid #E8E4D8; border-radius: 8px; padding: 16px 20px 16px 36px; margin: 18px 0; font-size: 14.5px; color: #333; list-style: disc outside; }
.article-body :deep(.article-list li) { display: list-item; margin-bottom: 8px; line-height: 1.55; }
.article-body :deep(.article-list li:last-child) { margin-bottom: 0; }
/* FAQ */
.article-body :deep(.faq-item) { border-bottom: 1px solid #eee; padding: 14px 0; }
.article-body :deep(.faq-item:first-child) { border-top: 1px solid #eee; }
.article-body :deep(.faq-item .question h4) { font-size: 15px; font-weight: 700; color: #111; margin: 0 0 6px; }
.article-body :deep(.faq-item .answer p) { font-size: 14px; color: #555; line-height: 1.6; margin: 0; }
/* References */
.article-body :deep(.article-sources) { margin: 28px 0 0; }
.article-body :deep(.article-sources h2) { margin-bottom: 6px; }
.article-body :deep(.article-sources .src-note) { font-size: 13px; color: #6b7a7d; margin-bottom: 10px; }
.article-body :deep(.article-sources ol), .article-body :deep(.article-sources ul) { margin: 0; padding-left: 22px; font-size: 14px; line-height: 1.7; color: #444; list-style: decimal outside; }
.article-body :deep(.article-sources li) { display: list-item; margin-bottom: 4px; }
.article-body :deep(.article-sources a) { color: var(--pop-blue,#316bff); text-decoration: underline; }

/* ----- Editor-seeded visual blocks (pop-* marker classes) -> publish design ----- */
/* Lists: the editor copy carries pop-article-list -> same boxed style as publish. */
.article-body :deep(ul.pop-article-list), .article-body :deep(ol.pop-article-list) { background: #FAFAF7; border: 1px solid #E8E4D8; border-radius: 8px; padding: 16px 20px 16px 36px; margin: 18px 0; font-size: 14.5px; color: #333; list-style: disc outside; }
.article-body :deep(.pop-article-list li) { display: list-item; margin-bottom: 8px; line-height: 1.55; }
.article-body :deep(.pop-article-list li:last-child) { margin-bottom: 0; }
/* FAQ rows (flat h3/p markup) -> publish .faq-item look. */
.article-body :deep(h2.pop-faq-title) { margin-bottom: 8px; }
.article-body :deep(h3.pop-faq-q) { border-top: 1px solid #eee; padding: 14px 0 6px; margin: 0; font-size: 15px; font-weight: 700; color: #111; }
.article-body :deep(p.pop-faq-a) { font-size: 14px; color: #555; line-height: 1.6; margin: 0; padding: 0 0 14px; border-bottom: 1px solid #eee; }
/* Form placeholder (editor-copy p.pop-form-ph + any surviving publish markup)
   -> publish dashed box. */
.article-body :deep(.article-form-ph), .article-body :deep(.dvb-form-ph), .article-body :deep(p.pop-form-ph) { display: flex; align-items: center; justify-content: center; gap: 10px; border: 1px dashed var(--border-strong, #ccc); border-radius: 10px; padding: 16px 18px; margin: 24px 0; color: var(--text-3, #6b7b7e); font-size: 13px; line-height: 1.5; background: #fff; clear: both; box-sizing: border-box; }

/* ----- TOC (nav.pop-toc — same design as publish); system block, not editable ----- */
.wg-block-system { cursor: default; }
.wg-block-system:hover { background: none; }
.article-body :deep(nav.pop-toc) { border: 1px solid var(--border, #e4e4e4); border-radius: 8px; background: var(--surface-2, #f7f7f7); padding: 16px 18px; margin: 0 0 22px; }
.article-body :deep(nav.pop-toc h2) { font-size: 14px; font-weight: 800; color: var(--text-2, #014751); margin: 0 0 12px; text-transform: uppercase; letter-spacing: .05em; }
.article-body :deep(nav.pop-toc .toc-list) { list-style: none; margin: 0; padding: 0; counter-reset: toc; }
.article-body :deep(nav.pop-toc .toc-list > li) { counter-increment: toc; margin: 0 0 9px; }
.article-body :deep(nav.pop-toc .toc-list > li:last-child) { margin-bottom: 0; }
.article-body :deep(nav.pop-toc .toc-list a) { display: inline-flex; gap: 10px; color: var(--text-2, #014751); font-weight: 600; font-size: 14px; text-decoration: none; }
.article-body :deep(nav.pop-toc ol.toc-list > li > a)::before { content: counter(toc) "."; color: var(--pop-orange, #ff5700); font-weight: 800; min-width: 20px; }
.article-body :deep(nav.pop-toc .toc-list a:hover) { color: var(--pop-orange, #ff5700); }
.article-body :deep(nav.pop-toc .toc-sub) { list-style: none; margin: 7px 0 2px 30px; padding: 0; }
.article-body :deep(nav.pop-toc .toc-sub a) { color: var(--text-3, #6b7b7e); font-weight: 500; font-size: 13px; text-decoration: none; display: inline-flex; gap: 8px; }
/* Mobile: collapsed by default, matching publish. */
.article-body.mobile :deep(nav.pop-toc .toc-list) { display: none; }
.article-body.mobile :deep(nav.pop-toc h2) { margin: 0; display: flex; align-items: center; justify-content: space-between; }
.article-body.mobile :deep(nav.pop-toc h2)::after { content: "▾"; color: var(--text-3, #6b7b7e); font-size: 12px; }

/* ----- Edit mode (Draft > Content UX: dblclick -> per-element textarea) ----- */
.wg-block { cursor: text; border-radius: 6px; transition: background 0.12s; }
.wg-block:hover { background: linear-gradient(0deg, rgba(255, 87, 0, 0.03), rgba(255, 87, 0, 0.03)); }
/* Orange editing card (Draft-step edit state): the rendered surface AND the
   Save/Cancel row live INSIDE the bordered box. */
.editing-area { cursor: auto; margin: 8px 0; background: #fff; border: 1px solid var(--pop-orange, #ff5700); border-radius: 10px; box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); padding: 12px 14px; }
.edit-surface { outline: none; min-height: 40px; }
/* Verbatim Draft-step BlockItem .edit-textarea */
.edit-textarea { width: 100%; box-sizing: border-box; min-height: 160px; resize: vertical; font-family: inherit; font-size: 13.5px; color: var(--text, #052e33); line-height: 1.6; border: 1px solid var(--pop-orange, #ff5700); border-radius: 8px; padding: 10px 12px; outline: none; box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }
/* Actions under the Draft-step dashed divider (BlockItem .block-actions-row) */
.edit-mode-actions { display: flex; gap: 8px; margin-top: 12px; padding-top: 12px; border-top: 1px dashed var(--border, #e4e4e4); }
.wg-btn { font-family: inherit; cursor: pointer; border-radius: 7px; font-weight: 600; font-size: 0.82rem; padding: 7px 14px; border: 1px solid var(--border-strong, #ccc); background: #fff; color: var(--text, #052e33); transition: all 0.15s; }
.wg-btn:hover { background: var(--surface-2, #f7f7f7); }
.wg-btn--primary { background: var(--pop-orange, #ff5700); border-color: var(--pop-orange, #ff5700); color: #fff; box-shadow: 0 2px 6px rgba(255, 87, 0, 0.25); }
.wg-btn--primary:hover { background: var(--pop-orange-hover, #e64e00); }
.wg-btn--danger { margin-left: auto; color: #c0392b; border-color: currentColor; }
.wg-btn--danger:hover { background: #fdecea; }
/* HTML tab edit: same pattern in the dark source styling */
.html-editing-area { padding: 12px 16px; background: #0f1f24; }
.html-edit-textarea { background: #0f1f24; color: #cfe5ea; font-size: 12px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; border-color: var(--pop-orange, #ff5700); min-height: 200px; overflow: hidden; resize: none; }
.article-html-view { cursor: text; }
</style>
